import { useState } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import { Banknote, CalendarClock, type LucideIcon } from 'lucide-react';

import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import ScraperListingCard from '@modules/_shared/components/scraper-listing-card/ScraperListingCard';
import { PropertyTypesProvider } from '@modules/_shared/providers/PropertyTypesProvider';
import { IconBadge } from '@modules/_shared/shared/components/IconBadge';
import type { ListingItem } from '@modules/_shared/utils/listingDataTransformers';
import ListingComparableDetails from '@modules/reports/cma-reports/step-listing-compare/ListingComparableDetails';
import { useLocale } from '@shared/hooks/locale';
import { getCurrencyByCountryCode } from '@shared/LocaleContext';
import { formatPrice } from '@shared/utils/format-price';

import { InfoTip } from './ChartControls';
import {
  type ComparableRow,
  type ListingComparables,
  useListingComparables,
} from './useListingComparables';

const CARD_WIDTH = 260;

// A single median figure shown above a carousel, in the icon-badge stat style
// shared with the visibility dashboard (badge + bold value + labelled caption).
const MedianStat = ({
  icon,
  label,
  help,
  value,
}: {
  icon: LucideIcon;
  label: string;
  help: string;
  value: string;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
    <IconBadge icon={icon} color="primary.main" />
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '16px', fontWeight: 800, lineHeight: 1.15 }}>
        {value}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.375 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 500, color: 'text.secondary', lineHeight: 1.2 }}
        >
          {label}
        </Typography>
        <InfoTip title={help} />
      </Box>
    </Box>
  </Box>
);

const ComparableCarousel = ({
  rows,
  showOfflineSince,
  onSelect,
}: {
  rows: ComparableRow[];
  showOfflineSince: boolean;
  onSelect: (row: ComparableRow) => void;
}) => (
  <Box
    sx={{
      display: 'flex',
      gap: 1.5,
      overflowX: 'auto',
      pb: 1,
      // Keep the scrollbar subtle but present on touch/desktop.
      scrollbarWidth: 'thin',
    }}
  >
    {rows.map(row => (
      <Box
        key={row.id}
        sx={{
          width: CARD_WIDTH,
          minWidth: CARD_WIDTH,
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <ScraperListingCard
          listing={row.listing as unknown as ListingItem}
          variant="vertical"
          imageLoading="lazy"
          onClick={() => onSelect(row)}
          showDistance
          distanceInMeters={row.distance_in_meters}
          showPricePerSqm
          showDaysOnMarket
          showOfflineSince={showOfflineSince}
          compact
        />
      </Box>
    ))}
  </Box>
);

const ComparableSection = ({
  title,
  rows,
  domLabel,
  domHelp,
  domMedian,
  ppsmMedian,
  showOfflineSince,
  onSelect,
}: {
  title: string;
  rows: ComparableRow[];
  domLabel: string;
  domHelp: string;
  domMedian: number | null;
  ppsmMedian: number | null;
  showOfflineSince: boolean;
  onSelect: (row: ComparableRow) => void;
}) => {
  const { t, locale, countryCode } = useLocale();
  const currency = getCurrencyByCountryCode(countryCode);

  if (rows.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1.5}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, fontSize: '14px' }}
      >
        {`${title} (${rows.length})`}
      </Typography>
      <Stack direction="row" spacing={4}>
        <MedianStat
          icon={CalendarClock}
          label={domLabel}
          help={domHelp}
          value={
            domMedian != null
              ? t('{{count}} day', { count: Math.round(domMedian) })
              : '—'
          }
        />
        <MedianStat
          icon={Banknote}
          label={t('Price per m²')}
          help={t('Median asking price per m² across these comparables.')}
          value={
            ppsmMedian != null
              ? `${formatPrice(Math.round(ppsmMedian), locale, currency)} / m²`
              : '—'
          }
        />
      </Stack>
      <ComparableCarousel
        rows={rows}
        showOfflineSince={showOfflineSince}
        onSelect={onSelect}
      />
    </Stack>
  );
};

export const ListingComparablesCard = ({ lotId }: { lotId: string }) => {
  const { t } = useLocale();
  const comparables: ListingComparables = useListingComparables(lotId);
  // Scraper listing opened in the CMA details drawer (online + offline alike).
  const [selected, setSelected] = useState<{
    id: number;
    stateId: string;
  } | null>(null);

  // No comparable set possible (unsupported country, missing coordinates or
  // property type) — hide the card entirely rather than show an empty shell.
  if (comparables.state === 'unavailable') {
    return null;
  }

  const openDrawer = (row: ComparableRow) => {
    const listing = row.listing;
    if (listing?.id != null) {
      setSelected({ id: listing.id, stateId: listing.state_id ?? '' });
    }
  };

  const isEmpty =
    comparables.state === 'ready' &&
    comparables.online.length === 0 &&
    comparables.offline.length === 0;

  return (
    // ScraperListingCard reads property-type labels from this context, which
    // (unlike the CMA route) the listing detail tree doesn't mount globally.
    <PropertyTypesProvider>
      <ListingAccordion
        title={t('Comparable listings')}
        loading={comparables.state === 'loading'}
      >
        {comparables.state === 'error' && (
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {t('Failed to load data')}
          </Typography>
        )}
        {comparables.state === 'ready' && (
          <Stack spacing={3.5}>
            {isEmpty ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('No comparable listings found nearby.')}
              </Typography>
            ) : (
              <>
                <ComparableSection
                  title={t('Online')}
                  rows={comparables.online}
                  domLabel={t('Days on market')}
                  domHelp={t(
                    'Median time the still-online comparables have been on the market.',
                  )}
                  domMedian={comparables.onlineDomMedian}
                  ppsmMedian={comparables.onlinePpsmMedian}
                  showOfflineSince={false}
                  onSelect={openDrawer}
                />
                <ComparableSection
                  title={t('Offline')}
                  rows={comparables.offline}
                  domLabel={t('Time online before offline')}
                  domHelp={t(
                    'Median time the offline comparables stayed online before going offline.',
                  )}
                  domMedian={comparables.offlineDurationMedian}
                  ppsmMedian={comparables.offlinePpsmMedian}
                  showOfflineSince
                  onSelect={openDrawer}
                />
              </>
            )}
          </Stack>
        )}
      </ListingAccordion>
      {selected != null && (
        <ListingComparableDetails
          listingId={selected.id}
          stateId={selected.stateId}
          onClose={() => setSelected(null)}
        />
      )}
    </PropertyTypesProvider>
  );
};
